import isPlainObject from 'lodash/isPlainObject';

import { pickPackableQuantity, readQuantity, toFiniteQuantity } from 'support/helpers/packingList/packableQuantity';
import { WebEDIRowData } from 'support/types/webediTable';

type Identifiers = {
  sender_internal?: string | null;
  gtin?: string | null;
  receiver_internal?: string | null;
};

type ItemShape = { identifiers?: Identifiers };

export type LineItemGroup = {
  indices: Array<number>;
  rows: Array<WebEDIRowData>;
  mergedRow: WebEDIRowData;
  /** The group's shipped total. */
  totalQuantity: number;
  /** How much of this product the builder offers for placement — see pickPackableQuantity. */
  packableQuantity: number;
};

const IDENTIFIER_FIELDS = ['gtin', 'receiver_internal', 'sender_internal'] as const;

/**
 * Deep-merges grouped rows with the first row authoritative: a value present on an
 * earlier row is always kept, and only the gaps (undefined/null/'') are filled from
 * later rows — recursing into nested objects so a single missing identifier can come
 * from a different member of the group.
 */
export const mergeLineItemRows = (rows: Array<WebEDIRowData>): WebEDIRowData =>
  rows.reduce((merged, row) => deepFill(merged, row) as WebEDIRowData);

/**
 * Overlap-must-agree: two items are the same product when every identifier field
 * present on BOTH sides has the same value, and at least one field is present on
 * both. A field absent on either side never blocks the match, so a row carrying
 * only a GTIN is the same product as one carrying that GTIN plus a receiver
 * article number.
 */
export const identifiersMatch = (a: Identifiers, b: Identifiers): boolean => {
  let sharedField = false;
  for (const field of IDENTIFIER_FIELDS) {
    const aValue = a[field];
    const bValue = b[field];
    if (present(aValue) && present(bValue)) {
      if (aValue !== bValue) {
        return false;
      }
      sharedField = true;
    }
  }
  return sharedField;
};

type GroupAccumulator = Omit<LineItemGroup, 'mergedRow' | 'packableQuantity'>;

/**
 * Groups only TOP-LEVEL line-item rows (those without a `parent_line_item`) by product identity,
 * preserving each row's original array index. Sub line items are intentionally excluded: their
 * identity is their relative path under the parent, not just their product identifiers. Children
 * are reconstructed and their proportional copies aggregated by the caller (see
 * initializeFromData), not here.
 */
export const groupTopLevelLineItems = (
  lineItems: Array<WebEDIRowData>,
  quantityFieldPath: string,
): Array<LineItemGroup> => {
  const groups: Array<GroupAccumulator> = [];
  lineItems.forEach((row, index) => {
    if (row._internal?.parent_line_item) {
      return;
    }
    const identifiers = getIdentifiers(row);
    const quantity = readQuantity(row[quantityFieldPath]);
    const match = groups.find((group) => identifiersMatch(getIdentifiers(group.rows[0]), identifiers));
    if (match) {
      match.indices.push(index);
      match.rows.push(row);
      match.totalQuantity += quantity;
    } else {
      groups.push({ indices: [index], rows: [row], totalQuantity: quantity });
    }
  });
  return groups.map((group) => {
    const mergedRow = mergeLineItemRows(group.rows);
    const orderedQuantity = sumOrderedQuantity(group.rows);
    return {
      ...group,
      mergedRow: orderedQuantity === undefined ? mergedRow : { ...mergedRow, ordered_quantity: orderedQuantity },
      packableQuantity: pickPackableQuantity(orderedQuantity, group.totalQuantity),
    };
  });
};

// undefined, null and '' all count as "no value" — the gap a later row may fill.
const present = (value: unknown): boolean => value != null && value !== '';

// lodash defaultsDeep is first-source-wins like we want, but treats '' and null as real
// values, so it would leave those gaps unfilled; this recursion fills them instead.
const deepFill = (base: unknown, fill: unknown): unknown => {
  if (isPlainObject(base) && isPlainObject(fill)) {
    const baseObject = base as Record<string, unknown>;
    const fillObject = fill as Record<string, unknown>;
    const result: Record<string, unknown> = { ...baseObject };
    for (const key of Object.keys(fillObject)) {
      result[key] = deepFill(baseObject[key], fillObject[key]);
    }
    return result;
  }
  return present(base) ? base : fill;
};

const getIdentifiers = (row: WebEDIRowData): Identifiers => (row.item as ItemShape | undefined)?.identifiers ?? {};

// ordered_quantity is an additive per-line quantity (e.g. useOrderSummary sums it across line
// items), so a collapsed product must carry the group total rather than only the first row's
// value — otherwise the ordered quantity of every duplicate past the first is silently lost.
// Returns undefined when no member carried the key, so mergeLineItemRows keeps it absent.
const sumOrderedQuantity = (rows: Array<WebEDIRowData>): number | undefined =>
  rows.some((row) => toFiniteQuantity(row.ordered_quantity) !== undefined)
    ? rows.reduce((sum, row) => sum + readQuantity(row.ordered_quantity), 0)
    : undefined;
